import { Download, Mail } from "lucide-react";
import { API } from "@/components/Admin/api";
import { formatDate } from "@/components/Admin/format";

const FILTERS = Object.freeze([
    { id: "all", label: "All" },
    { id: "demo", label: "Demo" },
    { id: "capability", label: "Capability" },
]);

const COLUMNS = Object.freeze([
    "Received",
    "Type",
    "Name",
    "Email",
    "Company",
    "Role",
    "Project",
    "Country",
    "Stack",
    "Workforce",
]);

export default function SubmissionsTable({ rows, loading, filter, onFilter }) {
    const exportCsv = () => {
        // Cookie-based auth means the request from a new tab still carries
        // the admin session automatically (same-origin + path=/api/admin).
        const url = `${API}/admin/export.csv?type=${encodeURIComponent(filter)}`;
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return (
        <section className="mt-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                <div>
                    <h2 className="font-display text-3xl leading-none">
                        Submissions
                    </h2>
                    <p className="mt-1.5 text-sm text-zinc-400">
                        Newest first. Filter by stream, then export to CSV for
                        procurement / CRM ingestion.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-lg border border-white/15 bg-white/[0.03] p-1">
                        {FILTERS.map((f) => (
                            <button
                                key={f.id}
                                onClick={() => onFilter(f.id)}
                                className={`px-3 py-1.5 text-sm rounded-md ${
                                    filter === f.id
                                        ? "bg-[#1FA8FF] text-white"
                                        : "text-zinc-300 hover:text-white"
                                }`}
                                data-testid={`filter-${f.id}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={exportCsv}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1FA8FF] text-white text-sm font-semibold hover:bg-[#0C84D4]"
                        data-testid="admin-export-csv"
                    >
                        <Download className="w-4 h-4" />
                        Export {filter === "all" ? "all" : filter} CSV
                    </button>
                </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-white/[0.04] text-left text-[10px] uppercase tracking-widest text-zinc-400">
                            <tr>
                                {COLUMNS.map((c) => (
                                    <th
                                        key={c}
                                        className="px-4 py-3 font-medium whitespace-nowrap"
                                    >
                                        {c}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td
                                        colSpan={COLUMNS.length}
                                        className="px-4 py-12 text-center text-zinc-500"
                                    >
                                        No submissions yet.
                                    </td>
                                </tr>
                            )}
                            {rows.map((r) => (
                                <SubmissionRow key={r.id} row={r} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

function SubmissionRow({ row }) {
    return (
        <tr className="hover:bg-white/[0.03]" data-testid={`row-${row.id}`}>
            <Td>{formatDate(row.received_at)}</Td>
            <Td>
                <TypeBadge type={row.type} />
            </Td>
            <Td>{row.name || "—"}</Td>
            <Td>
                <a
                    href={`mailto:${row.work_email}`}
                    className="text-[#1FA8FF] hover:underline inline-flex items-center gap-1.5"
                >
                    <Mail className="w-3 h-3" />
                    {row.work_email}
                </a>
            </Td>
            <Td>{row.company || "—"}</Td>
            <Td>{row.role || "—"}</Td>
            <Td>{row.project_name || "—"}</Td>
            <Td>{row.country || "—"}</Td>
            <Td className="max-w-[200px] truncate">
                {row.current_compliance_stack || "—"}
            </Td>
            <Td>{row.workforce_size || "—"}</Td>
        </tr>
    );
}

function Td({ children, className = "" }) {
    return (
        <td
            className={`px-4 py-3 text-zinc-300 align-top whitespace-nowrap ${className}`}
        >
            {children}
        </td>
    );
}

function TypeBadge({ type }) {
    const isDemo = type === "book_demo";
    return (
        <span
            className={`inline-flex items-center text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${
                isDemo
                    ? "text-[#1FA8FF] border-[#1FA8FF]/30 bg-[#1FA8FF]/10"
                    : "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10"
            }`}
        >
            {isDemo ? "Demo" : "Capability"}
        </span>
    );
}
